import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const Settings = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Password change state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Permissions state (for masteradmin)
  const [adminPermissions, setAdminPermissions] = useState({
    canViewUsers: true,
    canCreateUsers: true,
    canEditUsers: true,
    canDeleteUsers: true,
    canViewLogs: true,
    canViewDashboard: true,
    canViewSettings: true,
    canManageAutomation: true
  });
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchAdminPermissions = useCallback(async () => {
    try {
      setLoadingPermissions(true);
      const response = await axios.get(`${API_BASE_URL}/permissions/admin`, getAuthHeaders());
      setAdminPermissions(response.data.permissions);
    } catch (err) {
      console.error('Failed to fetch admin permissions:', err);
      setError(err.response?.data?.message || 'Failed to fetch admin permissions');
    } finally {
      setLoadingPermissions(false);
    }
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, getAuthHeaders());
      setUser(response.data);
      setTwoFactorEnabled(response.data.twoFactorEnabled || false);
      
      // If masteradmin, fetch permissions
      if (response.data.role === 'masteradmin') {
        fetchAdminPermissions();
      }
    } catch (err) {
      console.error('Failed to fetch user info:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAdminPermissions]);

  const handlePermissionChange = (permission, value) => {
    setAdminPermissions(prev => ({
      ...prev,
      [permission]: value
    }));
  };

  const handleSavePermissions = async () => {
    try {
      setSavingPermissions(true);
      setError('');
      await axios.put(`${API_BASE_URL}/permissions/admin`, { permissions: adminPermissions }, getAuthHeaders());
      setSuccess('Admin permissions updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update admin permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  const handleSetup2FA = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/setup-2fa`, {}, getAuthHeaders());
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setSetupDialogOpen(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to setup 2FA');
    }
  };

  const handleEnable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/auth/enable-2fa`,
        { secret, token: verificationCode },
        getAuthHeaders()
      );
      setSuccess('2FA enabled successfully!');
      setSetupDialogOpen(false);
      setVerificationCode('');
      fetchUserInfo();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to enable 2FA');
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA? This will make your account less secure.')) {
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/auth/disable-2fa`, {}, getAuthHeaders());
      setSuccess('2FA disabled successfully');
      fetchUserInfo();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to disable 2FA');
    }
  };

  // Password validation helper
  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return 'Password must contain at least one special character (!@#$%^&*()_+-=[]{};\':"|,.<>/?))';
    }
    return '';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setError('');
    setSuccess('');

    // Validate all fields are filled
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    // Validate new password matches confirmation
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match');
      return;
    }

    // Validate password strength
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    // Check if new password is different from old password
    if (oldPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setChangingPassword(true);

    try {
      await axios.post(
        `${API_BASE_URL}/auth/change-password`,
        { oldPassword, newPassword },
        getAuthHeaders()
      );
      setSuccess('Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '2rem', 
      maxWidth: '900px', 
      margin: '0 auto',
      minHeight: 'calc(100vh - 100px)'
    }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ 
          marginBottom: '0.5rem', 
          fontSize: '2rem', 
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Settings
        </h1>
        <p style={{ 
          marginBottom: 0, 
          color: '#6c757d',
          fontSize: '0.95rem'
        }}>
          Configure admin dashboard settings
        </p>
      </div>

      {error && (
        <Message 
          severity="error" 
          text={error} 
          style={{ marginBottom: '1.5rem' }} 
          onClose={() => setError('')} 
        />
      )}

      {success && (
        <Message 
          severity="success" 
          text={success} 
          style={{ marginBottom: '1.5rem' }} 
          onClose={() => setSuccess('')} 
        />
      )}

      {/* User Info */}
      <Card 
        style={{ 
          marginBottom: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <div style={{ 
          padding: '1.5rem',
          borderBottom: '1px solid #e9ecef',
          marginBottom: '1rem'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '1.25rem', 
            fontWeight: '600',
            color: '#2c3e50',
            marginBottom: '1.5rem'
          }}>
            Account Information
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'auto 1fr', 
            gap: '1rem 1.5rem',
            alignItems: 'center'
          }}>
            <div style={{ 
              fontWeight: '600', 
              color: '#495057',
              fontSize: '0.95rem'
            }}>
              Email:
            </div>
            <div style={{ 
              color: '#212529',
              fontSize: '0.95rem'
            }}>
              {user?.email}
            </div>
            <div style={{ 
              fontWeight: '600', 
              color: '#495057',
              fontSize: '0.95rem'
            }}>
              Role:
            </div>
            <div>
              <span style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                backgroundColor: user?.role === 'masteradmin' ? '#fff3cd' : '#e7f3ff',
                color: user?.role === 'masteradmin' ? '#856404' : '#0066cc',
                borderRadius: '12px',
                fontSize: '0.875rem',
                fontWeight: '600',
                textTransform: 'capitalize'
              }}>
                {user?.role === 'masteradmin' ? 'Master Admin' : 'Admin'}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Change Password */}
      <Card style={{ 
        marginBottom: '1.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ padding: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            marginBottom: '1rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #e9ecef'
          }}>
            <i className="pi pi-lock" style={{ 
              marginRight: '0.75rem', 
              fontSize: '1.25rem',
              color: '#0066cc',
              display: 'inline-flex',
              alignItems: 'center',
              verticalAlign: 'middle',
              lineHeight: '1'
            }}></i>
            <h3 style={{ 
              margin: 0, 
              fontSize: '1.25rem', 
              fontWeight: '600',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center'
            }}>
              Change Password
            </h3>
          </div>
          <p style={{ 
            marginBottom: '1.5rem', 
            color: '#6c757d',
            fontSize: '0.95rem'
          }}>
            Update your account password. Make sure to use a strong password.
          </p>

          <form onSubmit={handleChangePassword}>
            <div className="p-field" style={{ marginBottom: '1.5rem' }}>
              <label 
                htmlFor="oldPassword" 
                className="p-d-block" 
                style={{ 
                  marginBottom: '0.5rem', 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}
              >
                Current Password
              </label>
              <Password
                id="oldPassword"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                style={{ width: '100%' }}
                feedback={false}
                disabled={changingPassword}
                toggleMask
                inputStyle={{ width: '100%', padding: '0.75rem' }}
              />
            </div>

            <div className="p-field" style={{ marginBottom: '1.5rem' }}>
              <label 
                htmlFor="newPassword" 
                className="p-d-block" 
                style={{ 
                  marginBottom: '0.5rem', 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}
              >
                New Password
              </label>
              <Password
                id="newPassword"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                style={{ width: '100%' }}
                feedback={true}
                disabled={changingPassword}
                toggleMask
                inputStyle={{ width: '100%', padding: '0.75rem' }}
              />
              <small className="p-d-block" style={{ 
                marginTop: '0.5rem', 
                color: '#6c757d',
                fontSize: '0.875rem'
              }}>
                Must be at least 8 characters with uppercase, lowercase, number, and special character
              </small>
            </div>

            <div className="p-field" style={{ marginBottom: '1.5rem' }}>
              <label 
                htmlFor="confirmPassword" 
                className="p-d-block" 
                style={{ 
                  marginBottom: '0.5rem', 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}
              >
                Confirm New Password
              </label>
              <Password
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                style={{ width: '100%' }}
                feedback={false}
                disabled={changingPassword}
                toggleMask
                inputStyle={{ width: '100%', padding: '0.75rem' }}
              />
              {confirmPassword !== '' && newPassword !== confirmPassword && (
                <small className="p-d-block" style={{ 
                  marginTop: '0.5rem', 
                  color: '#dc3545',
                  fontSize: '0.875rem'
                }}>
                  Passwords do not match
                </small>
              )}
            </div>
            
            {passwordError && (
              <Message 
                severity="error" 
                text={passwordError} 
                style={{ marginBottom: '1rem' }} 
              />
            )}

            <Button
              type="submit"
              label="Change Password"
              icon={changingPassword ? null : "pi pi-save"}
              iconPos="left"
              disabled={changingPassword || !oldPassword || !newPassword || !confirmPassword}
              loading={changingPassword}
              className="p-button-lg"
              style={{ 
                marginTop: '0.5rem',
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: '500',
                minWidth: '180px'
              }}
            />
          </form>
        </div>
      </Card>

      {/* 2FA Settings */}
      <Card style={{ 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ padding: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            marginBottom: '1rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #e9ecef'
          }}>
            <i className="pi pi-shield" style={{ 
              marginRight: '0.75rem', 
              fontSize: '1.25rem',
              color: '#28a745',
              display: 'inline-flex',
              alignItems: 'center',
              verticalAlign: 'middle',
              lineHeight: '1'
            }}></i>
            <h3 style={{ 
              margin: 0, 
              fontSize: '1.25rem', 
              fontWeight: '600',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center'
            }}>
              Two-Factor Authentication (2FA)
            </h3>
          </div>
          <p style={{ 
            marginBottom: '1.5rem', 
            color: '#6c757d',
            fontSize: '0.95rem'
          }}>
            Add an extra layer of security to your account using an authenticator app.
          </p>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '1rem',
            padding: '1rem',
            backgroundColor: twoFactorEnabled ? '#d4edda' : '#f8f9fa',
            borderRadius: '8px',
            border: `1px solid ${twoFactorEnabled ? '#c3e6cb' : '#dee2e6'}`
          }}>
            <InputSwitch
              checked={twoFactorEnabled}
              onChange={(e) => {
                if (e.value) {
                  handleSetup2FA();
                } else {
                  handleDisable2FA();
                }
              }}
              style={{ marginRight: '1rem' }}
            />
            <label style={{ 
              fontWeight: '600',
              color: twoFactorEnabled ? '#155724' : '#495057',
              fontSize: '0.95rem'
            }}>
              {twoFactorEnabled ? '2FA Enabled' : '2FA Disabled'}
            </label>
          </div>

          {twoFactorEnabled && (
            <Message 
              severity="success" 
              text="Two-factor authentication is enabled. You'll need to enter a code from your authenticator app when logging in." 
              style={{ marginTop: '1rem' }}
            />
          )}
        </div>
      </Card>

      {/* 2FA Setup Dialog */}
      <Dialog
        header={
          <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0.5rem' }}>
            <i className="pi pi-shield" style={{ marginRight: '0.75rem', fontSize: '1.25rem' }}></i>
            <span style={{ fontSize: '1.25rem', fontWeight: '600' }}>Setup Two-Factor Authentication</span>
          </div>
        }
        visible={setupDialogOpen}
        style={{ width: '90%', maxWidth: '580px' }}
        contentStyle={{ padding: '2rem 2.5rem', maxHeight: '85vh', overflowY: 'auto' }}
        breakpoints={{ '960px': '95vw', '640px': '95vw' }}
        onHide={() => {
          setSetupDialogOpen(false);
          setVerificationCode('');
          setError('');
        }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1.5rem 2.5rem' }}>
            <Button 
              label="Cancel" 
              icon="pi pi-times" 
              iconPos="left"
              onClick={() => {
                setSetupDialogOpen(false);
                setVerificationCode('');
                setError('');
              }} 
              className="p-button-text p-button-lg"
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem'
              }}
            />
            <Button
              label="Enable 2FA"
              icon="pi pi-check"
              iconPos="left"
              onClick={handleEnable2FA}
              disabled={verificationCode.length !== 6}
              className="p-button-lg"
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                minWidth: '140px'
              }}
            />
          </div>
        }
      >
        <div style={{ padding: '1rem 0' }}>
          <p style={{ 
            marginBottom: '2rem', 
            color: '#495057',
            fontSize: '1rem',
            lineHeight: '1.6'
          }}>
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </p>
          
          {qrCode && (
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '2.5rem',
              padding: '1.5rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <img 
                src={qrCode} 
                alt="QR Code" 
                style={{ 
                  maxWidth: '100%',
                  width: '256px',
                  height: '256px',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  padding: '0.75rem'
                }} 
              />
            </div>
          )}
          
          <div style={{ marginBottom: '2.5rem' }}>
            <p style={{ 
              marginBottom: '1rem', 
              color: '#495057',
              fontSize: '1rem',
              fontWeight: '600'
            }}>
              Or enter this code manually:
            </p>
            <div style={{
              padding: '1rem 1.25rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
              fontFamily: 'monospace',
              fontSize: '0.95rem',
              wordBreak: 'break-all',
              color: '#212529',
              fontWeight: '600',
              textAlign: 'center',
              cursor: 'pointer',
              userSelect: 'all',
              lineHeight: '1.5'
            }}
            onClick={() => {
              navigator.clipboard.writeText(secret);
              setSuccess('Secret code copied to clipboard!');
              setTimeout(() => setSuccess(''), 3000);
            }}
            title="Click to copy"
            >
              {secret}
            </div>
            <small style={{ 
              display: 'block',
              marginTop: '0.75rem',
              color: '#6c757d',
              fontSize: '0.875rem',
              textAlign: 'center'
            }}>
              Click to copy
            </small>
          </div>
          
          <div className="p-field">
            <label 
              htmlFor="verificationCode" 
              className="p-d-block" 
              style={{ 
                marginBottom: '1rem', 
                fontWeight: '600',
                color: '#495057',
                fontSize: '1rem'
              }}
            >
              Enter 6-digit verification code
            </label>
            <InputText
              id="verificationCode"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={{ 
                width: '100%', 
                textAlign: 'center', 
                fontSize: '1.75rem', 
                letterSpacing: '0.75rem',
                padding: '1rem',
                fontWeight: '600',
                fontFamily: 'monospace',
                marginBottom: '0.5rem'
              }}
              autoFocus
            />
            <small style={{ 
              display: 'block',
              marginTop: '0.75rem',
              color: '#6c757d',
              fontSize: '0.875rem',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              Enter the 6-digit code from your authenticator app
            </small>
          </div>
        </div>
      </Dialog>

      {/* Admin Permissions (Master Admin Only) */}
      {user?.role === 'masteradmin' && (
        <Card style={{ 
          marginTop: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ padding: '1.5rem' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              marginBottom: '1rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #e9ecef'
            }}>
              <i className="pi pi-cog" style={{ 
                marginRight: '0.75rem', 
                fontSize: '1.25rem',
                color: '#856404',
                display: 'inline-flex',
                alignItems: 'center',
                verticalAlign: 'middle',
                lineHeight: '1'
              }}></i>
              <h3 style={{ 
                margin: 0, 
                fontSize: '1.25rem', 
                fontWeight: '600',
                color: '#2c3e50',
                display: 'flex',
                alignItems: 'center'
              }}>
                Admin Permissions Control
              </h3>
            </div>
            <p style={{ 
              marginBottom: '1.5rem', 
              color: '#6c757d',
              fontSize: '0.95rem'
            }}>
              Control what actions regular admins can perform in the dashboard.
            </p>

            {loadingPermissions ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <ProgressSpinner />
              </div>
            ) : (
              <div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <label style={{ 
                      fontWeight: '500', 
                      color: '#495057',
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      View Users
                    </label>
                    <InputSwitch
                      checked={adminPermissions.canViewUsers}
                      onChange={(e) => handlePermissionChange('canViewUsers', e.value)}
                      disabled={savingPermissions}
                    />
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <label style={{ 
                      fontWeight: '500', 
                      color: '#495057',
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      Create Users
                    </label>
                    <InputSwitch
                      checked={adminPermissions.canCreateUsers}
                      onChange={(e) => handlePermissionChange('canCreateUsers', e.value)}
                      disabled={savingPermissions}
                    />
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <label style={{ 
                      fontWeight: '500', 
                      color: '#495057',
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      Edit Users
                    </label>
                    <InputSwitch
                      checked={adminPermissions.canEditUsers}
                      onChange={(e) => handlePermissionChange('canEditUsers', e.value)}
                      disabled={savingPermissions}
                    />
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <label style={{ 
                      fontWeight: '500', 
                      color: '#495057',
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      Delete Users
                    </label>
                    <InputSwitch
                      checked={adminPermissions.canDeleteUsers}
                      onChange={(e) => handlePermissionChange('canDeleteUsers', e.value)}
                      disabled={savingPermissions}
                    />
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <label style={{ 
                      fontWeight: '500', 
                      color: '#495057',
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      View Logs
                    </label>
                    <InputSwitch
                      checked={adminPermissions.canViewLogs}
                      onChange={(e) => handlePermissionChange('canViewLogs', e.value)}
                      disabled={savingPermissions}
                    />
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <label style={{ 
                      fontWeight: '500', 
                      color: '#495057',
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      View Dashboard
                    </label>
                    <InputSwitch
                      checked={adminPermissions.canViewDashboard}
                      onChange={(e) => handlePermissionChange('canViewDashboard', e.value)}
                      disabled={savingPermissions}
                    />
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <label style={{ 
                      fontWeight: '500', 
                      color: '#495057',
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      View Settings
                    </label>
                    <InputSwitch
                      checked={adminPermissions.canViewSettings}
                      onChange={(e) => handlePermissionChange('canViewSettings', e.value)}
                      disabled={savingPermissions}
                    />
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <label style={{ 
                      fontWeight: '500', 
                      color: '#495057',
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      Manage Automation
                    </label>
                    <InputSwitch
                      checked={adminPermissions.canManageAutomation}
                      onChange={(e) => handlePermissionChange('canManageAutomation', e.value)}
                      disabled={savingPermissions}
                    />
                  </div>
                </div>

                <Button
                  label="Save Permissions"
                  icon={savingPermissions ? null : "pi pi-save"}
                  iconPos="left"
                  onClick={handleSavePermissions}
                  disabled={savingPermissions}
                  loading={savingPermissions}
                  className="p-button-lg"
                  style={{ 
                    padding: '0.75rem 2rem',
                    fontSize: '1rem',
                    fontWeight: '500',
                    minWidth: '180px'
                  }}
                />
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default Settings;
